import {
  Box3,
  ConeGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  type Object3D,
  Vector3,
} from "three";

/** Resolved against the deploy base so the game also works from a subpath. */
export const AIRCRAFT_MODEL_URL = `${import.meta.env.BASE_URL}models/toy-plane.glb`;

export interface AircraftTemplate {
  /** Model centred on its own origin, ready to be cloned per aircraft. */
  readonly template: Object3D;
  /** Length along the model's own forward axis, used to normalise scale. */
  readonly templateLength: number;
}

export interface AssetLoaderOptions {
  readonly load: (url: string) => Promise<Object3D>;
  readonly fallback: () => Object3D;
  readonly report: (error: unknown) => void;
}

/** Centre a loaded model and measure it. */
export function prepareTemplate(scene: Object3D): AircraftTemplate {
  scene.updateMatrixWorld(true);
  const box = new Box3().setFromObject(scene);
  const centre = box.getCenter(new Vector3());
  const size = box.getSize(new Vector3());
  scene.position.sub(centre);
  scene.updateMatrixWorld(true);
  return { template: scene, templateLength: Math.max(size.z, 0.001) };
}

export class AssetLoader {
  private cached?: Promise<AircraftTemplate>;

  constructor(private readonly options: AssetLoaderOptions) {}

  /** Cached; a failed load degrades to placeholder geometry, never throws. */
  loadAircraftTemplate(): Promise<AircraftTemplate> {
    if (!this.cached) {
      this.cached = this.options
        .load(AIRCRAFT_MODEL_URL)
        .catch((error: unknown) => {
          this.options.report(error);
          return this.options.fallback();
        })
        .then(prepareTemplate);
    }
    return this.cached;
  }
}

/** Stand-in used when the GLB cannot be decoded (older mobile WebGL stacks). */
export function generatedAircraft(): Object3D {
  const group = new Group();
  const body = new Mesh(
    new ConeGeometry(0.45, 1.6, 8),
    new MeshLambertMaterial({ name: "toyplane", color: 0xf5f5f5 }),
  );
  group.add(body);
  return group;
}

/**
 * The GLTF loader and its meshopt decoder are a few hundred kilobytes that
 * only matter once, after the airport is already on screen. Pulling them in
 * dynamically keeps them off the first paint's critical path.
 */
async function loadGltfScene(url: string): Promise<Object3D> {
  const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
    import("three/examples/jsm/loaders/GLTFLoader.js"),
    import("three/examples/jsm/libs/meshopt_decoder.module.js"),
  ]);
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  return (await loader.loadAsync(url)).scene;
}

export function createThreeAssetLoader(): AssetLoader {
  return new AssetLoader({
    load: loadGltfScene,
    fallback: generatedAircraft,
    report: (error) => {
      console.warn(
        "Aircraft model unavailable; using fallback geometry",
        error,
      );
    },
  });
}
